import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { useStaffNotificationUnreadCountQuery } from "@/v2-backoffice-client/notification/unreadCount/notification.unreadCount.query"

export type NotificationUnreadCountBadgeProps = ComponentProps<"span">

/**
 * Unread counter on the sidebar bell. Fetches once for the initial
 * value; every later change is pushed into the Apollo cache by the SSE
 * stream (useStaffNotificationStream), so there is no polling here.
 * Renders nothing when everything is read — same quiet-by-default
 * behavior as the layer review badge.
 */
function NotificationUnreadCountBadge(
  props: NotificationUnreadCountBadgeProps,
) {
  const { className, ...spanProps } = props

  const { data } = useStaffNotificationUnreadCountQuery()
  const count = data?.getStaffNotificationUnreadCount ?? 0

  if (count === 0) return null

  return (
    <span
      className={cn(
        "notification-unread-count-badge",
        "inline-flex items-center justify-center h-4 min-w-4 px-px rounded-full",
        "bg-destructive text-destructive-foreground",
        "text-[8px] font-semibold leading-none tabular-nums",
        className,
      )}
      {...spanProps}
    >
      {count > 99 ? "99+" : count}
    </span>
  )
}

export default NotificationUnreadCountBadge
